import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  fr: {
    loading: "Vérification du paiement…",
    paymentSuccess: "Paiement confirmé",
    paymentPending: "Paiement en cours…",
    paymentFailed: "Paiement non finalisé",
    pendingMsg: "Ton paiement est en cours de validation. Cette page se rafraîchira automatiquement.",
    failedMsg: "Si le montant a été prélevé, il sera remboursé sous 5 jours ouvrables.",
    orderRef: "Référence",
    eventTitle: "Événement",
    eventDate: "Date",
    yourTickets: "Tes billets",
    customerInfo: "Acheteur",
    total: "Total payé",
    important: "Important",
    importantMsg: "Présente ce courriel ou cette page à l'entrée. Tes billets sont aussi envoyés à ton adresse courriel.",
    backHome: "← Retour à l'accueil",
    eventPage: "Voir l'événement",
    by: "Présenté par",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
  en: {
    loading: "Verifying payment…",
    paymentSuccess: "Payment confirmed",
    paymentPending: "Payment processing…",
    paymentFailed: "Payment not finalized",
    pendingMsg: "Your payment is being validated. This page will refresh automatically.",
    failedMsg: "If the amount was charged, it will be refunded within 5 business days.",
    orderRef: "Reference",
    eventTitle: "Event",
    eventDate: "Date",
    yourTickets: "Your tickets",
    customerInfo: "Buyer",
    total: "Total paid",
    important: "Important",
    importantMsg: "Show this email or page at the entrance. Tickets are also sent to your email.",
    backHome: "← Back to home",
    eventPage: "View event",
    by: "Presented by",
    org: "Institut Impact Élite Martial · KAIZEN System",
  },
};

export default function TicketingOrderConfirmation() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get("lang") === "en" ? "en" : "fr";
  const t = T[lang];
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/ticketing/public/order/${sessionId}`);
        setData(r.data);
        if (r.data?.order?.payment_status !== "paid" && tries < 8) {
          timer = setTimeout(() => setTries((n) => n + 1), 2500);
        }
      } catch (e) {
        setData({ error: true });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, tries]);

  if (!data) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center" data-testid="confirmation-loading">
        {t.loading}
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4">
        <div className="text-2xl font-bold">❌ {t.paymentFailed}</div>
        <Link to="/" className="text-amber-400 hover:underline">{t.backHome}</Link>
      </div>
    );
  }

  const paid = data.order?.payment_status === "paid";
  const order = data.order;
  const event = data.event;
  const items = data.items || [];

  return (
    <div className="min-h-screen bg-black text-white" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Status hero */}
        <div className={`border rounded-2xl p-8 text-center ${paid ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
          <div className="text-5xl mb-4">{paid ? "✅" : "⏳"}</div>
          <div className="text-[10px] tracking-[0.4em] text-zinc-400 mb-2">{lang === "fr" ? "BILLETTERIE KAIZEN" : "KAIZEN TICKETING"}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="confirmation-status">
            {paid ? t.paymentSuccess : t.paymentPending}
          </h1>
          {!paid && (
            <div className="mt-3 text-sm text-amber-200" data-testid="pending-msg">{t.pendingMsg}</div>
          )}
        </div>

        {/* Order recap */}
        {order && (
          <div className="mt-8 space-y-4">
            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">{t.eventTitle.toUpperCase()}</div>
              <div className="text-xl font-bold" data-testid="confirm-event-title">{event && (lang === "fr" ? event.title_fr : event.title_en)}</div>
              <div className="text-sm text-zinc-400 mt-1">
                📅 {event?.date} {event?.time ? `· ${event.time}` : ""}
                {event?.venue_name ? <> · 📍 {event.venue_name}{event.venue_city ? `, ${event.venue_city}` : ""}</> : null}
              </div>
            </div>

            {/* Tickets */}
            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">{t.yourTickets.toUpperCase()}</div>
              <div className="space-y-2" data-testid="confirm-items">
                {items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-zinc-900 last:border-0 py-2">
                    <div>
                      <div className="text-sm font-semibold">{lang === "fr" ? it.name_fr : it.name_en}</div>
                      <div className="text-[10px] text-zinc-500">{it.quantity} × {it.unit_price_cad.toFixed(2)} $</div>
                    </div>
                    <div className="text-sm font-bold text-amber-300">{it.subtotal_cad.toFixed(2)} $</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-amber-500/20 pt-3">
                <span className="text-sm text-zinc-400">{t.total}</span>
                <span className="text-2xl font-bold text-amber-300" data-testid="confirm-total">{order.total_cad.toFixed(2)} $ CAD</span>
              </div>
            </div>

            {/* Buyer info */}
            <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40">
              <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">{t.customerInfo.toUpperCase()}</div>
              <div className="text-sm">{order.customer_name}</div>
              <div className="text-xs text-zinc-400 mt-1">{order.customer_email}</div>
              {order.customer_phone && <div className="text-xs text-zinc-400">{order.customer_phone}</div>}
              <div className="text-[10px] text-zinc-600 mt-3 font-mono">{t.orderRef}: {order.id}</div>
            </div>

            {/* Important */}
            {paid && (
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-2xl p-5">
                <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-1">⚠️ {t.important.toUpperCase()}</div>
                <div className="text-sm text-amber-100">{t.importantMsg}</div>
              </div>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {event?.slug && (
            <Link to={`/billetterie/${event.slug}`} className="text-sm text-amber-400 hover:text-amber-300 border border-amber-500/40 rounded-full px-4 py-2">
              {t.eventPage}
            </Link>
          )}
          <Link to="/" className="text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-full px-4 py-2">
            {t.backHome}
          </Link>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-zinc-500 text-xs">
          <div>{t.by}</div>
          <div className="text-amber-400 mt-1 tracking-[0.2em] font-semibold">{t.org}</div>
        </div>
      </div>
    </div>
  );
}
